import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, apiErrorMessage } from "../lib/api";

/**
 * Iter 91 — Admin Credit-Ledger History modal.
 *
 * Opens as a SEPARATE window from the Active Streamer review modal. Read-only
 * projection over the existing audit log + creditTopups collection served by
 * GET /api/admin/streamers/{sid}/credit-history. Newest first. Positive
 * whitelist on the server — no VG-formula / player-rebate leakage.
 *
 * Terminology preserved: Credits / Platform Account entitlement (never
 * deposit/escrow/custody/wallet-balance).
 */

const TYPE_META = {
  grant:             { tone: "ok",     label: "grant" },
  remove:            { tone: "danger", label: "remove" },
  topup_confirmed:   { tone: "ok",     label: "topup confirmed" },
  topup_rejected:    { tone: "danger", label: "topup rejected" },
  contract_reserve:  { tone: "warn",   label: "contract reserve" },
  contract_release:  { tone: "accent", label: "contract release" },
  money_back:        { tone: "accent", label: "money-back" },
  correction:        { tone: "default",label: "correction" },
};

const TONE_CLASSES = {
  ok:      "border-emerald-500/50 text-emerald-400",
  danger:  "border-[#F26D6D]/60 text-[#F26D6D]",
  warn:    "border-[#F5B841]/60 text-[#F5B841]",
  accent:  "border-[#2DD4A7]/50 text-[#2DD4A7]",
  default: "border-white/20 text-[#E8ECF4]",
};

function TypeBadge({ type }) {
  const m = TYPE_META[type] || { tone: "default", label: type || "—" };
  return (
    <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${TONE_CLASSES[m.tone]}`}>
      {m.label}
    </span>
  );
}

function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function DeltaCell({ n }) {
  if (n === null || n === undefined || Number(n) === 0) return <span className="text-[#8B97AE]">—</span>;
  const v = Number(n);
  const cls = v > 0 ? "text-emerald-400" : "text-[#F26D6D]";
  return <span className={`font-mono tabular-nums ${cls}`}>{v > 0 ? "+" : ""}{fmtNum(v)}</span>;
}

export default function CreditHistoryModal({ streamer, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(async () => {
    if (!streamer?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/streamers/${streamer.id}/credit-history`);
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [streamer?.id]);
  useEffect(() => { load(); }, [load]);

  if (!streamer) return null;

  const entries = (data?.entries || []).filter((e) => typeFilter === "all" || e.type === typeFilter);
  const types = Array.from(new Set((data?.entries || []).map((e) => e.type))).sort();

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="credit-history-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl bg-[#0B1220] border border-white/20 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Admin · Credit ledger history</div>
            <div className="font-display font-bold text-lg text-[#E8ECF4]" data-testid="credit-history-streamer">{streamer.displayName || streamer.alias || streamer.email}</div>
          </div>
          <button data-testid="credit-history-close" onClick={onClose} className="text-[#8B97AE] hover:text-white p-1"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-3">
          {loading ? (
            <div className="text-xs text-[#8B97AE]" data-testid="credit-history-loading">Loading…</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Filter by type</label>
                <select
                  data-testid="credit-history-type-filter"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="rounded-none bg-transparent border border-white/20 h-8 px-2 font-mono text-xs text-[#E8ECF4]"
                >
                  <option value="all" className="bg-[#0B1220]">all ({(data?.entries || []).length})</option>
                  {types.map((t) => (
                    <option key={t} value={t} className="bg-[#0B1220]">{TYPE_META[t]?.label || t}</option>
                  ))}
                </select>
                <button
                  data-testid="credit-history-refresh"
                  onClick={load}
                  className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#F5B841] hover:text-[#2DD4A7]"
                >Refresh</button>
              </div>

              <div className="border border-white/10 max-h-[65vh] overflow-y-auto">
                <table className="w-full text-xs" data-testid="credit-history-table">
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] sticky top-0 bg-[#0B1220] border-b border-white/10">
                    <tr>
                      <th className="text-left p-2">Time</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-right p-2">Δ Available</th>
                      <th className="text-right p-2">Bal. after</th>
                      <th className="text-right p-2">Δ Reserved</th>
                      <th className="text-right p-2">Δ Consumed</th>
                      <th className="text-left p-2">Reason / reference</th>
                      <th className="text-left p-2">Actor</th>
                      <th className="text-left p-2">Linked</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {entries.length === 0 ? (
                      <tr><td colSpan={9} className="p-4 text-center text-[#8B97AE]" data-testid="credit-history-empty">No credit-ledger movements recorded for this streamer.</td></tr>
                    ) : entries.map((e, i) => (
                      <tr key={`${e.timestamp}-${i}`} className="border-b border-white/5" data-testid={`credit-history-row-${i}`}>
                        <td className="p-2 text-[#8B97AE]">{(e.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                        <td className="p-2"><TypeBadge type={e.type} /></td>
                        <td className="p-2 text-right"><DeltaCell n={e.delta} /></td>
                        <td className="p-2 text-right text-[#E8ECF4]">{e.balance_after === null || e.balance_after === undefined ? <span className="text-[#8B97AE]">—</span> : fmtNum(e.balance_after)}</td>
                        <td className="p-2 text-right"><DeltaCell n={e.reserved_delta} /></td>
                        <td className="p-2 text-right"><DeltaCell n={e.consumed_delta} /></td>
                        <td className="p-2 text-[#E8ECF4] max-w-[240px] truncate" title={e.reason}>{e.reason || "—"}</td>
                        <td className="p-2 text-[#8B97AE]">{typeof e.actor === "string" ? e.actor.slice(0, 8) : (e.actor || "system")}</td>
                        <td className="p-2 text-[#8B97AE]">
                          {e.linked_ref
                            ? <span title={e.linked_ref.id}>{e.linked_ref.kind}:{(e.linked_ref.id || "").slice(0, 8)}</span>
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data?.coverage_note && (
                <div className="text-[10px] font-mono text-[#8B97AE]" data-testid="credit-history-coverage-note">
                  {data.coverage_note}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
